<template>
  <v-dialog v-model="model" width="500" persistent>
    <v-card style="border-radius: 10px">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h6 font-weight-bold">Record Audio Introduction</span>
        <v-btn icon size="small" @click="cancelRecording">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div class="text-body-2 mb-4">
          Please keep in mind the following instructions:
        </div>
        <ul class="text-body-2 mb-4 pl-4" style="list-style-type: none">
          <li>1. Please be in a noise-free environment.</li>
          <li>2. Speak clearly and make sure you can be heard.</li>
        </ul>

        <div
          class="d-flex justify-center align-center mt-4"
          style="background: #f1f1f1; height: 180px; border-radius: 8px; position: relative;"
        >
          <div v-if="!isRecording && !audioUrl">
            <v-btn color="red" dark @click="startRecording">
              Start Recording
            </v-btn>
          </div>
          <div v-else-if="isRecording">
            <v-btn color="red" dark @click="stopRecording">
              Stop Recording
            </v-btn>
          </div>
          <div v-else class="d-flex flex-column align-center">
            <audio :src="audioUrl" controls></audio>
            <div class="mt-2 text-caption text-grey-darken-1">
              Preview your recording above.
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="d-flex justify-end">
        <v-btn variant="outlined" @click="cancelRecording" :disabled="isRecording">
          Cancel
        </v-btn>
        <v-btn color="primary" :disabled="!recordedBlob" @click="saveAudio">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from "vue";
import { useStore } from "vuex";

const props = defineProps({ modelValue: Boolean });
const emit = defineEmits(["update:modelValue"]);
const store = useStore();

const model = ref(props.modelValue);
watch(() => props.modelValue, (val) => (model.value = val));
watch(model, (val) => emit("update:modelValue", val));

let mediaRecorder = null;
let audioChunks = [];

const isRecording = ref(false);
const recordedBlob = ref(null);
const audioUrl = ref("");

async function startRecording() {
  try {
    recordedBlob.value = null;
    audioUrl.value = "";

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaRecorder = new MediaRecorder(stream);
    mediaRecorder.ondataavailable = (e) => {
      audioChunks.push(e.data);
    };
    mediaRecorder.onstop = () => {
      const blob = new Blob(audioChunks, { type: "audio/webm" });
      recordedBlob.value = blob;
      audioUrl.value = URL.createObjectURL(blob);
      audioChunks = [];
      isRecording.value = false;
      stream.getTracks().forEach((t) => t.stop());
    };
    mediaRecorder.start();
    isRecording.value = true;
  } catch (err) {
    console.error("⛔️ Could not start audio recording:", err);
  }
}

function stopRecording() {
  if (mediaRecorder && isRecording.value) {
    mediaRecorder.stop();
  }
}

function cancelRecording() {
  if (mediaRecorder && isRecording.value) {
    mediaRecorder.stop();
  }
  resetAll();
  close();
}

function close() {
  model.value = false;
}

// On “Save” we persist both the Blob (in Vuex and IndexedDB) and the filename (in Vuex/localStorage)
async function saveAudio() {
  if (!recordedBlob.value) return;

  const blob = recordedBlob.value;
  const filename = `audio_${Date.now()}.webm`;

  // 1) Save Blob in Vuex state (in-memory)
  store.commit("profile/SET_AUDIO_BLOB", blob);

  // 2) Save metadata + store Blob in IndexedDB via action
  await store.dispatch("profile/saveAudioIntro", {
    blob,
    filename,
  });
  // store.audioMeta is now { filename }, store.audioBlob is the actual Blob

  store.dispatch("alert/success", "Audio introduction saved");
  close();
}

function resetAll() {
  isRecording.value = false;
  recordedBlob.value = null;
  audioUrl.value = "";
}

onBeforeUnmount(() => {
  // Clean up if the user never clicked Stop
  if (mediaRecorder && isRecording.value) {
    mediaRecorder.stop();
  }
  resetAll();
});

// On open, we can show a message if audioMeta exists, and we rehydrate the Blob
watch(
  () => model.value,
  async (isOpen) => {
    if (isOpen) {
      const meta = store.state.profile.audioMeta;
      if (meta && meta.filename) {
        // The Blob should have been rehydrated by store.loadFromLocalStorage()
        // in the parent onMounted. So just do nothing; the user can see
        // “Already recorded: filename” in console or UI if you add it.
      }
      resetAll();
    }
  }
);
</script>

<style scoped>
.text-caption {
  color: rgba(0, 0, 0, 0.6);
}
</style>
